import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector, connect } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, BaseMapItem, MAP_DEFAULT_VAR } from 'types';
import { basemaps } from 'app/contants';
import { setPivateTeamBaseMap } from 'redux/app/actions';
import { setBaseMap } from 'redux/session/actions';
import { setTeamBoundaryToggle } from 'redux/team/actions';
import { InlineLoading, Button, Toggle } from 'carbon-components-react';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import { setIsOpenMapStylePanel } from 'redux/map/actions';
import { Close16 } from '@carbon/icons-react';
import BaseMapOptionItem from './item';
import { useSaga } from 'use-reducer-saga';
import { getBaseMapsStart } from './localStore/actions';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { selectTeamBoundaryToggle } from 'redux/team/selectors';

const SwitcherPanel: React.FC<Props> = ({
  user,
  currentTeamId,
  baseMap,
  isOpen,
  showMasking,
  clusterHandler,
  clusterVisibleRef,
  showLegend
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { basemapOptions: options } = localStore;
  const teamBoudaryToggle = useSelector(selectTeamBoundaryToggle);
  const selectBaseMap = useCallback(
    (item: BaseMapItem) => {
      dispatch(
        setBaseMap({
          id: item.id,
          fromPortal: item.fromPortal,
          isCustom: false,
          theme: item.theme
        })
      );
      dispatch(setIsOpenMapStylePanel(false));
      if (user && !currentTeamId) {
        dispatch(
          setPivateTeamBaseMap({
            userId: user.id,
            baseMap: item.id
          })
        );
      }
    },
    [dispatch, user, currentTeamId]
  );

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (containerRef.current && evt.target) {
        if (!containerRef.current.contains(evt.target)) {
          dispatch(setIsOpenMapStylePanel(false));
        }
      }
    },
    [containerRef, dispatch]
  );

  const teamBoundaryHandler = (flag: boolean) => {
    dispatch(setTeamBoundaryToggle(flag));
  };

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  useComponentMount(() => {
    options?.length <= 0 && localDispatch(getBaseMapsStart(basemaps));
  });

  return (
    <>
      {isOpen && (
        <div className="map-style-controller-modal">
          <div className="map-style-controller-panel" ref={containerRef}>
            <div className="map-style-controller-panel-header">
              <div className="label">Map style</div>
              <Button
                size="small"
                kind="ghost"
                onClick={() => dispatch(setIsOpenMapStylePanel(false))}
              >
                <Close16 />
              </Button>
            </div>
            <div className="map-style-base-map-container">
              {options && options.length > 0 ? (
                <>
                  {options.map((item: BaseMapItem, inx: number) => {
                    return (
                      <BaseMapOptionItem
                        key={inx}
                        item={item}
                        selectedId={baseMap?.id || MAP_DEFAULT_VAR.BASEMAP}
                        selectBaseMap={selectBaseMap}
                      />
                    );
                  })}
                </>
              ) : (
                <div className="map-style-base-map-loading">
                  <InlineLoading description="Loading basemaps ..." />
                </div>
              )}
            </div>
            {(typeof clusterHandler === 'function' ||
              (showMasking && currentTeamId && currentTeamId > 0)) && (
              <div className="map-style-toggle-container">
                {typeof clusterHandler === 'function' && (
                  <div className="map-style-toggle cluster">
                    <Toggle
                      id={'clusterToggle'}
                      aria-label="Clusters"
                      labelText="Clusters"
                      labelA=""
                      labelB=""
                      defaultToggled={clusterVisibleRef?.current}
                      onToggle={(checked: boolean) => clusterHandler(checked)}
                    />
                  </div>
                )}
                {showMasking && currentTeamId && currentTeamId > 0 && (
                  <div className="map-style-toggle boundary">
                    <Toggle
                      id={'teamBoudaryToggle'}
                      aria-label="Team boundary"
                      labelText="Team boundary"
                      labelA=""
                      labelB=""
                      defaultToggled={teamBoudaryToggle}
                      onToggle={(checked: boolean) =>
                        teamBoundaryHandler(checked)
                      }
                    />
                  </div>
                )}
              </div>
            )}
            {showLegend && (
              <div className="map-style-legend-container">{showLegend}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = ({ session, mapCache, teamCache }: RootState) => ({
  user: session.currentUser,
  currentTeamId: teamCache.currentTeamId,
  baseMap: session.baseMap,
  isOpen: mapCache.isOpenBaseMapPanel
});

const connector = connect(mapStateToProps);

type OwnProps = {
  clusterHandler?: Function;
  clusterVisibleRef?: React.MutableRefObject<boolean>;
  showMasking?: boolean;
  showLegend?: React.ReactElement;
};

type Props = CFCProps<typeof connector> & OwnProps;

export default React.memo(connector(SwitcherPanel));
